import { createContext, useMemo, useContext, useCallback } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useQuery } from '@tanstack/react-query';

// src/react/provider.tsx

// src/client.ts
function normalize(baseUrl) {
  const trimmed = baseUrl.replace(/\/+$/, "");
  if (trimmed.endsWith("/v1")) return trimmed;
  return `${trimmed}/v1`;
}
function resolveAssetUrl(raw, baseUrl) {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const host = baseUrl.replace(/\/+$/, "").replace(/\/v1$/, "");
  return `${host}${raw}`;
}
var BrandingClient = class {
  baseUrl;
  timeoutMs;
  externalSignal;
  constructor(opts) {
    this.baseUrl = normalize(opts.baseUrl);
    this.timeoutMs = opts.timeoutMs ?? 1500;
    this.externalSignal = opts.signal;
  }
  /** Resolve URL relativa do Core pra absoluta (útil pra <img src>). */
  resolveUrl(raw) {
    return resolveAssetUrl(raw, this.baseUrl);
  }
  /** GET /v1/branding/{appKey}. Lança no timeout/404/erro de rede. */
  async getAppBranding(appKey) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    const signal = this.externalSignal ? mergeSignals(controller.signal, this.externalSignal) : controller.signal;
    try {
      const res = await fetch(`${this.baseUrl}/branding/${appKey}`, {
        signal,
        headers: { Accept: "application/json" }
      });
      if (!res.ok) {
        throw new Error(`branding_http_${res.status}`);
      }
      return await res.json();
    } finally {
      clearTimeout(timer);
    }
  }
  /**
   * GET /v1/me/branding — branding **efetivo** (com cascata por empresa)
   * pra o user autenticado. Devolve textos + logos já resolvidos.
   *
   * Requer `bearer_token` JWT do user. Header `X-App-Key` identifica qual
   * app está pedindo (define qual override de white-label aplicar). Query
   * `company_id` opcional força uma empresa específica das memberships do
   * user (default: primeira membership).
   *
   * Segurança: response tem `Cache-Control: private, no-store` no Core —
   * proxies não cacheiam.
   */
  async getMyBranding(bearerToken, appKey, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    const signal = this.externalSignal ? mergeSignals(controller.signal, this.externalSignal) : controller.signal;
    try {
      const qs = options?.companyId ? `?company_id=${encodeURIComponent(options.companyId)}` : "";
      const res = await fetch(`${this.baseUrl}/me/branding${qs}`, {
        signal,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${bearerToken}`,
          "X-App-Key": appKey
        }
      });
      if (!res.ok) {
        throw new Error(`branding_http_${res.status}`);
      }
      return await res.json();
    } finally {
      clearTimeout(timer);
    }
  }
};
function mergeSignals(a, b) {
  if (a.aborted) return a;
  if (b.aborted) return b;
  const c = new AbortController();
  a.addEventListener("abort", () => c.abort(), { once: true });
  b.addEventListener("abort", () => c.abort(), { once: true });
  return c.signal;
}
function MirorLabsMark({
  strokeWidth = 23,
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      viewBox: "0 0 418 451",
      "aria-hidden": "true",
      className,
      ...rest,
      children: /* @__PURE__ */ jsxs("g", { fill: "none", strokeLinecap: "round", stroke: "currentColor", children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M 62.33 316.23 A 170 170 0 0 1 305.8 91.56",
            strokeWidth
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M 357.67 147.77 A 170 170 0 0 1 114.2 372.44",
            strokeWidth
          }
        ),
        /* @__PURE__ */ jsx(
          "line",
          {
            x1: "39",
            y1: "389",
            x2: "380",
            y2: "74",
            strokeWidth: strokeWidth + 1
          }
        )
      ] })
    }
  );
}
var DEFAULT_FALLBACK = /* @__PURE__ */ jsx(MirorLabsMark, { className: "h-full w-full", style: { height: "100%", width: "100%" } });
var BrandingContext = createContext(null);
function BrandingProvider({ config, children }) {
  const value = useMemo(() => {
    const fallback = config.fallback === void 0 ? DEFAULT_FALLBACK : config.fallback;
    const resolved = {
      baseUrl: config.baseUrl,
      appKey: config.appKey,
      timeoutMs: config.timeoutMs ?? 1500,
      fallback,
      getToken: config.getToken ?? null
    };
    return {
      config: resolved,
      client: new BrandingClient({
        baseUrl: resolved.baseUrl,
        timeoutMs: resolved.timeoutMs
      })
    };
  }, [
    config.baseUrl,
    config.appKey,
    config.timeoutMs,
    config.fallback,
    config.getToken
  ]);
  return /* @__PURE__ */ jsx(BrandingContext.Provider, { value, children });
}
function useBrandingContext() {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error(
      "useBrandingContext: faltou <BrandingProvider> na \xE1rvore do app."
    );
  }
  return ctx;
}
function useAppBranding(appKey) {
  const ctx = useBrandingContext();
  const key = appKey ?? ctx.config.appKey;
  return useQuery({
    queryKey: ["miror-branding", ctx.config.baseUrl, key],
    queryFn: () => ctx.client.getAppBranding(key),
    staleTime: 5 * 6e4,
    gcTime: 60 * 6e4,
    refetchOnWindowFocus: false
  });
}
function useEffectiveBranding(options) {
  const ctx = useBrandingContext();
  const key = options?.appKey ?? ctx.config.appKey;
  const getToken = ctx.config.getToken;
  return useQuery({
    queryKey: [
      "miror-effective-branding",
      ctx.config.baseUrl,
      key,
      options?.companyId ?? null
    ],
    queryFn: async () => {
      if (!getToken) {
        throw new Error(
          "useEffectiveBranding: faltou `getToken` no <BrandingProvider config={...}>."
        );
      }
      const token = getToken();
      if (!token) {
        throw new Error("missing_token");
      }
      return ctx.client.getMyBranding(token, key, {
        companyId: options?.companyId
      });
    },
    enabled: (options?.enabled ?? true) && !!getToken,
    staleTime: 6e4,
    gcTime: 60 * 6e4,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message === "missing_token") return false;
      return failureCount < 2;
    }
  });
}
function AppLogo({
  variant,
  appKey,
  className,
  style,
  fallback,
  alt
}) {
  const ctx = useBrandingContext();
  const { data } = useAppBranding(appKey);
  const asset = data?.[variant] ?? null;
  const url = asset ? ctx.client.resolveUrl(asset.url) : null;
  if (!url) {
    const fb = fallback ?? ctx.config.fallback;
    if (fb == null) return null;
    return /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": alt ? void 0 : "true",
        className,
        style: { display: "inline-grid", placeItems: "center", ...style },
        children: fb
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "img",
    {
      src: url,
      alt: alt ?? "",
      "aria-hidden": alt ? void 0 : "true",
      loading: "eager",
      decoding: "async",
      className,
      style: { objectFit: "contain", ...style }
    }
  );
}
var DefaultLogo = ({ className }) => /* @__PURE__ */ jsx(AppLogo, { variant: "symbol", className });
function defaultBuildSubtitle({
  suiteName,
  appVersion
}) {
  if (!suiteName) return void 0;
  if (!appVersion) return suiteName;
  return `${suiteName} \xB7 v${appVersion}`;
}
function useBrandingAsMirorBrand(options) {
  const ctx = useBrandingContext();
  const buildSubtitle = options?.buildSubtitle ?? defaultBuildSubtitle;
  const Logo = options?.Logo ?? DefaultLogo;
  const appKey = ctx.config.appKey;
  const eff = useEffectiveBranding({
    companyId: options?.companyId,
    enabled: options?.enabled
  });
  const symbolUrl = eff.data?.symbol ? ctx.client.resolveUrl(eff.data.symbol.url) : null;
  const EffectiveLogo = useCallback(
    ({ className }) => {
      if (symbolUrl) {
        return /* @__PURE__ */ jsx(
          "img",
          {
            src: symbolUrl,
            alt: "",
            "aria-hidden": "true",
            loading: "eager",
            decoding: "async",
            className,
            style: { objectFit: "contain" }
          }
        );
      }
      const Fallback = Logo;
      return /* @__PURE__ */ jsx(Fallback, { className });
    },
    [symbolUrl, Logo]
  );
  if (eff.data) {
    const subtitle = buildSubtitle({
      suiteName: eff.data.suite_name,
      appVersion: options?.appVersion
    });
    return {
      Logo: EffectiveLogo,
      appName: eff.data.app_name,
      suiteName: eff.data.suite_name,
      appSubtitle: subtitle,
      appVersion: options?.appVersion
    };
  }
  return {
    Logo: EffectiveLogo,
    appName: titleCase(appKey),
    suiteName: "Miror Labs Workspace",
    appSubtitle: buildSubtitle({
      suiteName: "Miror Labs Workspace",
      appVersion: options?.appVersion
    }),
    appVersion: options?.appVersion
  };
}
function titleCase(appKey) {
  return appKey.split(/[_-]/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}
function useResolveAssetUrl() {
  const { client } = useBrandingContext();
  return useCallback((raw) => client.resolveUrl(raw), [client]);
}

export { AppLogo, BrandingProvider, MirorLabsMark, useAppBranding, useBrandingAsMirorBrand, useBrandingContext, useEffectiveBranding, useResolveAssetUrl };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map